// Wireup helper for the IntelliSense diagnostics suppressor.
// PRD: Slice "Diagnostics Suppression Rewiring" — wraps the async parser
// recovery lookup + bus snapshot lookup + `filterDiagnostics` call so
// SqlMonaco.tsx stays thin. Used by the LSP `onDiagnostics` handler in
// `SqlMonaco.tsx`.
//
// GRA-7 Slice A3: this helper used to own a tree-sitter `Tree` (parsed via
// `parseBuffer`, deleted in `finally`). It now sources its R3 input from
// `buildErrorRecoveryView` — a per-engine `buildParsedDocument` call that
// returns the parser's recovery ranges instead of a raw `Tree`. Same WASM
// load cost, no tree-sitter dependency at this boundary.

import type { DatabaseEngineId } from '@/core/engines';

import { getSnapshot as getSchemaBusSnapshot } from '../schemaBus/schemaBus';

import { buildErrorRecoveryView } from './errorRecoveryViewBuilder';
import { filterDiagnostics, type IMarkerData } from './suppressor';

type MonacoLike = {
  readonly editor: { setModelMarkers: (model: unknown, owner: string, markers: IMarkerData[]) => void };
};

// `getVersionId` is a real Monaco `ITextModel` method — it bumps on every
// buffer edit. We capture it before the async parse and re-check it before
// writing markers so a buffer change during the await doesn't let
// stale-source-derived markers clobber the new buffer.
type ModelLike = { getValue(): string; getVersionId(): number };

/**
 * Async wireup helper. Computes the error-recovery view via the engine's
 * `buildParsedDocument` (when supported), looks up the schema bus snapshot,
 * runs `filterDiagnostics`, and writes the filtered markers via
 * `monaco.editor.setModelMarkers`.
 *
 * Failure modes (parser unavailable, mock engine, missing connection) all
 * fall back to "filter with whatever inputs we have" — never throws to the
 * caller. The marker-set is fire-and-forget from the LSP listener's
 * perspective.
 */
export async function applyDiagnosticsSuppressionAndSet(
  monaco: MonacoLike,
  model: ModelLike,
  markers: readonly IMarkerData[],
  engineId: DatabaseEngineId,
  connectionId: string | undefined,
  database: string | undefined,
  setMarkers: (
    monaco: MonacoLike,
    model: ModelLike,
    markers: IMarkerData[],
  ) => void = defaultSetMarkers,
): Promise<void> {
  if (markers.length === 0) {
    setMarkers(monaco, model, []);
    return;
  }
  const source = model.getValue();
  // Snapshot the model version alongside the source. `buildErrorRecoveryView`
  // below is async; if the user keeps typing during that gap the buffer (and
  // version) change, and the markers we'd compute describe `source` — now
  // stale. We re-check this before writing so we never clobber the new
  // buffer with old-source markers; the diagnostics pass triggered by the
  // new buffer sets the correct markers.
  const versionAtStart = model.getVersionId();

  const errorRecoveryView = await buildErrorRecoveryView(engineId, source);

  // Fast bail: the buffer moved on under us during the parse await, so the
  // markers we'd compute describe a now-stale source. The LSP emits fresh
  // diagnostics for the new buffer, which re-enter this helper.
  if (model.getVersionId() !== versionAtStart) return;

  const snapshot = connectionId
    ? getSchemaBusSnapshot(connectionId, database)
    : null;

  const filtered = filterDiagnostics(markers, {
    errorRecoveryView,
    snapshot,
    source,
    engineId,
    // R4 (PG19 SQL/PGQ) gate — the snapshot already carries the live server
    // version for the safety rail; reuse it here.
    serverVersion: snapshot?.serverVersion,
  });
  // Re-check immediately before the write: the load-bearing correctness
  // point. (`getSchemaBusSnapshot`/`filterDiagnostics` are synchronous, so
  // the version cannot change between the two checks — this guard is
  // intentional defense-in-depth kept at the write site.)
  if (model.getVersionId() !== versionAtStart) return;
  setMarkers(monaco, model, filtered);
}

function defaultSetMarkers(
  monaco: MonacoLike,
  model: ModelLike,
  markers: IMarkerData[],
): void {
  monaco.editor.setModelMarkers(model, 'sql', markers);
}
